import { useRouterState } from "@tanstack/react-router";
import { useLocale, useTranslations } from "use-intl";

import { Link } from "@/components/link";
import { Button } from "@/components/ui/button";
import { useAdvertisingConsent } from "@/hooks/use-advertising-consent";
import { useHydrated } from "@/hooks/use-hydrated";
import { shouldLoadAnalytics } from "@/lib/analytics";
import { buildLegalPagePath } from "@/lib/legal-page-path";
import { normalizeViewerLocale } from "@garimpa/lib/viewerPreferences";

export function AdvertisingConsentBanner() {
  const t = useTranslations("globals.advertisingConsent");
  const locale = normalizeViewerLocale(useLocale());
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { choice, choose } = useAdvertisingConsent();
  const hydrated = useHydrated();

  if (!shouldLoadAnalytics(pathname) || choice !== null) return null;

  return (
    <aside
      aria-labelledby="advertising-consent-title"
      className="fixed inset-x-4 bottom-20 z-[80] mx-auto max-w-3xl rounded-xl border border-border/80 bg-card p-4 shadow-2xl xl:bottom-6"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-2xl">
          <h2 id="advertising-consent-title" className="text-base font-bold text-foreground">
            {t("title")}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {t("description")}{" "}
            <Link
              className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
              to={buildLegalPagePath(locale, "privacy")}
            >
              {t("privacyLink")}
            </Link>
          </p>
        </div>
        <div className="grid shrink-0 gap-2 sm:grid-cols-2">
          <Button
            disabled={!hydrated}
            className="w-full sm:min-w-40"
            size="lg"
            variant="outline"
            onClick={() => choose("denied")}
          >
            {t("necessaryOnly")}
          </Button>
          <Button
            disabled={!hydrated}
            className="w-full font-bold sm:min-w-40"
            size="lg"
            onClick={() => choose("granted")}
          >
            {t("allowAdvertising")}
          </Button>
        </div>
      </div>
    </aside>
  );
}
